import { LitElement, html } from 'lit'
import { consume } from '@lit/context'
import { customElement, property } from 'lit/decorators.js'
import { breakpointContext, displayModeContext, isScrolledTopContext, isSubMenuOpenContext, isOutletNavigationOpenContext } from './context.ts'
import { TW, type Breakpoint } from '../../shared'

import logo from '../../../images/logo-autohaus-driveme-optimized.svg'
// import logoWhite from '../../../images/logo-white.svg'

const TwLitElement = TW(LitElement)

/**
 * Sample Lit Element in assets/lit
 */
@customElement('bmw-branding')
export class BmwBranding extends TwLitElement {

    @property({ type: String }) 
    headline = "BMW Mustermann"

    @property({ type: String }) 
    subheadline = "Musterhausten"
    
    @consume({context: displayModeContext, subscribe: true}) 
    private _displayMode: string = "white"

    @consume({context: breakpointContext, subscribe: true}) 
    private _breakpoint: Breakpoint | null = null

    @consume({context: isScrolledTopContext, subscribe: true})
    private _isScrolledTop: boolean = true

    @consume({context: isSubMenuOpenContext, subscribe: true})
    private _isSubMenuOpen: boolean = false

    @consume({context: isOutletNavigationOpenContext, subscribe: true})
    private _isOutletNavigationOpen: boolean = false

    private _breakpointDependentLogoSizes() {

        const xsLogoSize = [ 'w-[21.77vw]', 'h-[5.8vw]' ]

        if ( ! this._isScrolledTop ) {
            return this._breakpoint?.name === 'xs'
                ? xsLogoSize
                : [ 'w-[150px]', 'h-[40px]' ]
        }

        switch ( this._breakpoint?.name ) {
            case 'xs':
                return xsLogoSize
            case 'sm':
                return [ 'w-[125px]', 'h-[34px]' ]
            case 'md':
                return [ 'w-[148px]', 'h-[40px]' ]
            default: 
                return [ 'w-[167px]', 'h-[45px]' ]
        }
    }

    private _logoLinkClasses(): string[] {

        const isShowBrandLine = this._isScrolledTop && ! ( this._isOutletNavigationOpen || this._isSubMenuOpen )

        const brandLine = isShowBrandLine ? ' after:absolute after:block after:h-[10px] after:bottom-0 after:left-0 after:right-0 after:z-1 after:bg-primary ' : '' // blaue Linie drive ME
        let baseClasses: string[] = [ 'relative', 'block', 'px-6', 'transition-all', 'duration-300', 'ease-in-out', brandLine ]

        const isMobile = this._breakpoint?.name === 'xs' || this._breakpoint?.name === 'sm'

        if ( this._isScrolledTop || ! isMobile ) {
            baseClasses.push( 'pb-8', 'pt-8' )
        } else {
            baseClasses.push( 'py-3' )
        }

        if ( ! this._isScrolledTop ) {
            baseClasses.push( 'flex', 'items-center' )
        }

        if ( this._isSubMenuOpen || this._isOutletNavigationOpen || ! this._isScrolledTop ) {
            baseClasses = baseClasses.filter( item => item !== 'px-6' ) // Das Padding entfernen, damit das Logo bündig dargestellt wird
        }
        
        return this._isScrolledTop && this._displayMode !== 'white'
            ? [ ...baseClasses, 'bg-white' ]
            : [ ...baseClasses, 'bg-white/0' ]
    }

    render() 
    {
        return html`
            <a href="/" title="Zur Startseite" aria-label="Zur Startseite" class="${this._logoLinkClasses().join(' ')}">
                ${
                    this._displayMode === "white" 
                    ? html`<img src=${logo} alt="drive ME GmbH" width="424" height="80" class=${this._breakpointDependentLogoSizes().join(' ')} />` 
                    : html`<img src=${logo} alt="drive ME GmbH" width="424" height="80" class=${this._breakpointDependentLogoSizes().join(' ')} />` 
                }
            </a>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'bmw-branding': BmwBranding
    }
}
